import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  type CancellationPreview,
  type CancellationResult,
  checkCancellation,
  confirmCancellation,
  previewCancellation,
} from '@/lib/subscription-api'
import { getErrorMessage } from '@/lib/error-messages'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type Result = CancellationResult
type Preview = CancellationPreview
const date = (value: string) =>
  new Date(value).toLocaleString('de-AT', {
    timeZone: 'Europe/Vienna',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

export function SubscriptionCancellation({ planned }: { planned: boolean }) {
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const [dialogPlanned, setDialogPlanned] = useState(planned)
  const [confirmed, setConfirmed] = useState<string | null>(null)
  const [accepted, setAccepted] = useState<Result | null>(null)
  const handled = useRef<string | null>(null)
  const handleResult = useCallback((value: Result) => {
    setAccepted(value)
    if (
      !['applied', 'failed'].includes(value.status) ||
      handled.current === value.operationKey
    ) return
    handled.current = value.operationKey
    void client.invalidateQueries({
      predicate: (query) =>
        !['cancellation-preview', 'cancellation-status'].includes(
          String(query.queryKey[0]),
        ),
    })
    if (value.status === 'applied') {
      toast.success(
        value.action === 'resume'
          ? 'Die Kündigung wurde zurückgenommen. Stornierte Buchungen bleiben storniert.'
          : `Dein Abo endet am ${
            date(value.endsAt)
          }. ${value.cancelledBookings.length} Buchung(en) wurden sofort storniert.`,
      )
      setOpen(false)
    } else {
      toast.error(
        getErrorMessage(new Error(value.code ?? 'CANCELLATION_FAILED')) +
          (value.replacementRemoved
            ? ' Die bisherige Tarifvormerkung wurde aufgehoben und wird nicht wiederhergestellt.'
            : ''),
        { duration: 10000 },
      )
      setAccepted(null)
      setConfirmed(null)
      void client.invalidateQueries({ queryKey: ['cancellation-preview'] })
    }
  }, [client])
  const intent = dialogPlanned ? 'resume' : 'cancel'
  const preview = useQuery({
    queryKey: ['cancellation-preview', intent],
    queryFn: () => previewCancellation(intent),
    enabled: open && !accepted,
    staleTime: 0,
    retry: false,
  })
  const waiting = accepted ??
    (preview.data?.inProgress ? preview.data as Result : null)
  const polling = useQuery({
    queryKey: ['cancellation-status', waiting?.operationKey],
    queryFn: () => checkCancellation(waiting!.operationKey!),
    enabled: open &&
      Boolean(waiting && ['preparing', 'uncertain'].includes(waiting.status)),
    refetchInterval: (query) =>
      ['applied', 'failed'].includes(query.state.data?.status ?? '')
        ? false
        : 3000,
    retry: false,
  })
  const mutation = useMutation({
    mutationFn: () => confirmCancellation(preview.data!, intent),
    onSuccess: handleResult,
    onError: (error) => {
      toast.error(getErrorMessage(error))
      if (
        ['CANCELLATION_PREVIEW_CHANGED', 'CANCELLATION_PREVIEW_EXPIRED']
          .includes(error.message)
      ) {
        setConfirmed(null)
        void preview.refetch()
      }
    },
  })
  useEffect(() =>
    client.getQueryCache().subscribe((event) => {
      if (
        event.type === 'updated' && event.action.type === 'success' &&
        event.query.queryKey[0] === 'cancellation-preview' &&
        (event.query.state.data as Preview | undefined)?.alreadyCancelled
      ) {
        void client.invalidateQueries({
          predicate: (query) =>
            !['cancellation-preview', 'cancellation-status'].includes(
              String(query.queryKey[0]),
            ),
        })
      }
      if (
        event.type === 'updated' && event.action.type === 'success' &&
        event.query.queryKey[0] === 'cancellation-status' &&
        event.query.queryKey[1] === waiting?.operationKey
      ) {
        handleResult(event.query.state.data as Result)
      }
    }), [client, waiting?.operationKey, handleResult])
  const requiresConfirmation = Boolean(
    preview.data?.affectedBookings?.length || preview.data?.replacement,
  )
  const pending = Boolean(
    waiting && ['preparing', 'uncertain'].includes(waiting.status),
  )
  return (
    <>
      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={() => {
          setAccepted(null)
          setConfirmed(null)
          setDialogPlanned(planned)
          setOpen(true)
        }}
      >
        {planned ? 'Kündigung zurücknehmen' : 'Abo kündigen'}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          if (!mutation.isPending) setOpen(value)
        }}
      >
        <DialogContent className='max-h-[85vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>
              {dialogPlanned ? 'Kündigung zurücknehmen' : 'Abo kündigen'}
            </DialogTitle>
            <DialogDescription>
              {dialogPlanned
                ? 'Dein Abo wird danach wieder regulär verlängert.'
                : 'Prüfe das Abo-Ende und die Folgen für deine Buchungen.'}
            </DialogDescription>
          </DialogHeader>
          {preview.isPending && (
            <p role='status'>Kündigung und Buchungen werden geprüft …</p>
          )}
          {preview.error && (
            <p role='alert' className='text-sm text-destructive'>
              {getErrorMessage(preview.error)}
            </p>
          )}
          {pending && (
            <p role='status' className='text-sm'>
              Deine Bestätigung wird noch verarbeitet. Die Buchungen werden erst
              storniert, sobald die Kündigung bestätigt ist. Du kannst den
              Dialog später erneut öffnen.
            </p>
          )}
          {polling.error && (
            <p role='alert' className='text-sm text-destructive'>
              {getErrorMessage(polling.error)}{' '}
              Du kannst den Status später erneut prüfen.
            </p>
          )}
          {!pending && preview.data && (
            <div className='space-y-3 text-sm'>
              <p>
                Dein Abo{' '}
                {dialogPlanned ? 'war zur Kündigung vorgesehen' : 'endet'} am
                {' '}
                <strong>{date(preview.data.endsAt)}</strong>.
              </p>
              {preview.data.alreadyCancelled
                ? (
                  <p>
                    Die Kündigung ist bereits vorgemerkt. Zugehörige
                    Buchungsstornos wurden verarbeitet.
                  </p>
                )
                : dialogPlanned
                ? (
                  <p>
                    Bereits stornierte Buchungen und aufgehobene
                    Tarifvormerkungen werden nicht wiederhergestellt. Du kannst
                    verfügbare Termine erneut buchen.
                  </p>
                )
                : (
                  <>
                    <p>
                      Bis zum Abo-Ende bleiben dein Abo und die davor liegenden,
                      gedeckten Termine bestehen.
                    </p>
                    {preview.data.affectedBookings.length > 0
                      ? (
                        <>
                          <p className='font-semibold'>
                            Diese Buchungen ab dem Abo-Ende werden bei
                            erfolgreicher Kündigung sofort storniert,
                            einschließlich zugehöriger Gäste und Einladungen:
                          </p>
                          <ul className='list-disc space-y-1 pl-5'>
                            {preview.data.affectedBookings.map((booking) => (
                              <li key={booking.id}>
                                {booking.course_name} ·{' '}
                                {date(booking.starts_at)}
                              </li>
                            ))}
                          </ul>
                        </>
                      )
                      : <p>Es sind keine bestehenden Buchungen betroffen.</p>}
                    <p>
                      Vollständig mit Kaufguthaben bezahlte Buchungen bleiben
                      bestehen. Bei gemischter Finanzierung werden gekaufte
                      Anteile zurückerstattet. Zusätzliches Kaufguthaben wird
                      nicht automatisch eingesetzt.
                    </p>
                    <p>
                      Eine spätere Rücknahme der Kündigung stellt stornierte
                      Buchungen nicht wieder her.
                    </p>
                    {preview.data.replacement && (
                      <p>
                        Dein vorgemerkter Wechsel zu „{preview.data.replacement
                          .packageName}“ ab{' '}
                        {date(preview.data.replacement.effectiveAt)}{' '}
                        wird aufgehoben. Er wird auch dann nicht
                        wiederhergestellt, wenn die anschließende Kündigung
                        scheitert.
                      </p>
                    )}
                  </>
                )}
              {requiresConfirmation && !dialogPlanned && (
                <label className='flex items-start gap-2'>
                  <Checkbox
                    checked={confirmed === preview.data.previewId}
                    onCheckedChange={(checked) =>
                      setConfirmed(
                        checked === true ? preview.data!.previewId! : null,
                      )}
                  />
                  <span>
                    Ich bestätige die sofortigen Stornierungen und
                    gegebenenfalls das Aufheben meiner Tarifvormerkung.
                  </span>
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              disabled={mutation.isPending}
              onClick={() => setOpen(false)}
            >
              Schließen
            </Button>
            {!pending && !preview.data?.alreadyCancelled && (
              <Button
                type='button'
                variant={dialogPlanned ? 'default' : 'destructive'}
                disabled={!preview.data?.previewId || mutation.isPending ||
                  (requiresConfirmation &&
                    confirmed !== preview.data.previewId)}
                onClick={() => mutation.mutate()}
              >
                {mutation.isPending
                  ? 'Wird verarbeitet …'
                  : dialogPlanned
                  ? 'Kündigung verbindlich zurücknehmen'
                  : preview.data?.affectedBookings.length
                  ? 'Abo kündigen und Buchungen stornieren'
                  : 'Abo verbindlich kündigen'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
